import ExportButton from '../../components/ExportButton';
import usePageViewState from '../../components/usePageViewState';
import RowActionsMenu from '../../components/RowActionsMenu';
import { usePermissions } from '../../auth/permissions';
import { exportCustomers } from '../../utils/excelExport';
import '../../components/export-button.css';
import Toast from '../../components/Toast';
import CompactBreadcrumb from '../../components/CompactBreadcrumb';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, ListChecks, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import TableScroll from '../../components/TableScroll';
import { createCustomer, deleteCustomer, listCustomers, updateCustomer } from './customerService';
import CustomerModal, { CustomerLogo } from '../../components/CustomerModal';
import '../warehouses/warehouses.css';
import './customers.css';

const PAGE_SIZE = 10;


export default function CustomersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const writable = canWrite('Customers');
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [customerModal, setCustomerModal] = usePageViewState('customerModal', null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const customers = useMemo(() => records.filter(item => !item.is_deleted)
    .sort((left, right) => Number(right.id) - Number(left.id)), [records]);
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter(item => [item.name, item.mobile_number, item.address].some(value => value.toLowerCase().includes(term)));
  }, [customers, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const rows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    async function loadCustomers() {
      try {
        setRecords([]);
        const items = await listCustomers();
        if (!cancelled) setRecords(items);
      } catch (error) {
        if (!cancelled) {
          setRecords([]);
          showToast(error.message, 'error');
        }
      }
    }
    loadCustomers();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {

  }, [query, currentPage]);

  useEffect(() => {
    if (!deleteItem) return undefined;
    function handleKey(event) {
      if (event.key === 'Escape' && !deleting) closeDelete();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [deleteItem, deleting]);


  useEffect(() => () => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
  }, []);

  function updateQuery(value) {
    setQuery(value);
    setPage(1);

  }

  function showToast(message, tone = 'success', title) {
    setToast({ message, tone, title });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4000);
  }

  function openAdd() {
    if (!writable) return;

    setCustomerModal({ mode: 'add', customer: null });
  }

  function openEdit(selected) {
    if (!writable) return;

    if (selected) setCustomerModal({ mode: 'edit', customer: selected });
  }

  function openDelete(selected) {
    if (!writable) return;

    if (selected) setDeleteItem(selected);
  }

  function closeCustomerModal() {
    setCustomerModal(null);
  }

  function closeDelete() {
    if (deleting) return;
    setDeleteItem(null);
  }

  async function saveCustomer(payload) {
    if (!writable) return;
    if (customerModal?.mode === 'edit' && customerModal.customer) {
      const id = customerModal.customer.id;
      let saved;
      try {
        saved = await updateCustomer(id, payload);
      } catch (error) {
        if (error.status === 403 || error.status === 404) {
          setCustomerModal(null);
          showToast("You don't have permission to edit this customer.", 'error');
          return;
        }
        throw error;
      }
      setRecords(current => current.map(item => (
        item.id === id ? { ...item, ...payload, ...saved, id, is_deleted: false } : item
      )));
      showToast('Customer updated.');
      return;
    }

    const saved = await createCustomer(payload);
    if (!saved?.id) throw new Error('Customer was not saved to the database.');
    setRecords(current => [...current, { ...payload, ...saved, id: saved.id, is_deleted: false }]);
    setPage(1);
    showToast('Customer created.');
  }

  async function confirmDelete() {
    if (!writable) return;
    if (deleting || !deleteItem) return;
    setDeleting(true);
    try {
      await deleteCustomer(deleteItem.id);
      setRecords(current => current.map(item => (
        item.id === deleteItem.id ? { ...item, is_deleted: true } : item
      )));
      setDeleteItem(null);
      showToast('Customer deleted.');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setDeleteItem(null);
      setDeleting(false);
    }
  }

  return <section className="wh-page wh-list-page cu-page">
    <div className="wh-title">
      <div>
        {location.state?.fromDashboard && <CompactBreadcrumb parent="Dashboard" current="Customers" onNavigate={() => navigate('/dashboard')} />}
        <h1>Customers</h1>
      </div>
      <div className="wh-title-actions">
        <ExportButton onExport={format => exportCustomers(filtered, format).catch(error => showToast(error.message || 'Unable to export customers.', 'error'))}>Export</ExportButton>
        <button className="wh-add" type="button" disabled={!writable} onClick={openAdd}><Plus size={14} />Add Customer</button>
      </div>
    </div>
    <div className="cu-list">
      <div className="wh-panel">
      <div className="wh-filters">
        <label className="wh-search"><Search size={22} /><input type="search" aria-label="Search customers" placeholder="Search customers..." value={query} onChange={event => updateQuery(event.target.value)} /></label>

      </div>
      <TableScroll>
        <table className="wh-table cu-table">
          <thead>
            <tr>
              <th className="wh-actions-heading" aria-label="Actions" title="Actions"><ListChecks size={16} aria-hidden="true" /></th>
              <th>Logo</th>
              <th>Customer Name</th>
              <th>Mobile Number</th>
              <th>Address</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(item => <tr key={item.id}>
              <td className="wh-actions-cell" onClick={event => event.stopPropagation()}>
                <RowActionsMenu label={`Actions for ${item.name}`} actions={[
                  { label: 'View', icon: Eye, onClick: () => navigate(`/customers/${item.id}`) },
                  { label: 'Edit', icon: Pencil, disabled: !writable, onClick: () => openEdit(item) },
                  { label: 'Delete', icon: Trash2, danger: true, disabled: !writable, onClick: () => openDelete(item) },
                ]} />
              </td>
              <td><CustomerLogo name={item.name} logo={item.logo} /></td>
              <td><span className="wh-name">{item.name}</span></td>
              <td>{item.mobile_number || '\u2014'}</td>
              <td>{item.address || '\u2014'}</td>
            </tr>)}
            {!rows.length && <tr><td className="wh-empty" colSpan={5}>{customers.length ? 'No customers match the current search.' : 'No customers found.'}</td></tr>}
          </tbody>
        </table>
      </TableScroll>
      <div className="wh-pager">
        <p>Showing {start}{'\u2013'}{end} of {filtered.length} Customers</p>
        <div>
          <button type="button" disabled={currentPage === 1} onClick={() => { setPage(currentPage - 1); }}><ChevronLeft size={14} />Previous</button>
          {Array.from({ length: pageCount }, (_, index) => <button key={index + 1} type="button" className={`wh-page-number${currentPage === index + 1 ? ' current' : ''}`} aria-current={currentPage === index + 1 ? 'page' : undefined} onClick={() => { setPage(index + 1); }}>{index + 1}</button>)}
          <button type="button" disabled={currentPage === pageCount} onClick={() => { setPage(currentPage + 1); }}>Next<ChevronRight size={14} /></button>
        </div>
      </div>
      </div>
    </div>


    {writable && customerModal && <CustomerModal
      mode={customerModal.mode}
      customer={customerModal.customer}
      onClose={closeCustomerModal}
      onSubmit={saveCustomer}
    />}

    {writable && deleteItem && <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeDelete(); }}>
      <div className="wh-modal wh-modal-confirm" role="dialog" aria-modal="true" aria-labelledby="cu-delete-title">
        <h2 id="cu-delete-title">Delete Customer?</h2>
        <p className="wh-modal-copy">Are you sure you want to delete this customer?</p>
        <div className="wh-delete-target">
          <strong>{deleteItem.name}</strong>
          <span>{deleteItem.address || deleteItem.mobile_number}</span>
        </div>
        <div className="wh-modal-actions">
          <button className="wh-btn-ghost" type="button" onClick={closeDelete} disabled={deleting} autoFocus>Cancel</button>
          <button className="wh-btn-danger" type="button" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
        </div>
      </div>
    </div>}

    <Toast toast={toast} onClose={() => setToast(null)} />
  </section>;
}
